// models/api/deviceGroups.ts
import { getRequest, postRequest, putRequest, deleteRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import type { DeviceGroup } from 'lib/types/deviceGroups'

const orgHeader = (orgId: string) =>
  orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId }

// ---- helpers ----
const buildId = (g: any) =>
  String(
    g?.id ??
      g?.uuid ??
      g?.device_group_id ??
      `${g?.org_id ?? g?.orgId ?? 'no-org'}:${g?.name ?? 'no-name'}`
  )

const toDate = (v: unknown): Date | undefined => {
  if (!v) return undefined
  return v instanceof Date ? v : new Date(v as any)
}

type AnyAlert = {
  id?: string
  uuid?: string
  alert_source_id?: string
  name?: string
  deviceGroupId?: string
  device_group_id?: string
  [k: string]: any
}

const normalizeAlertSource = (a: AnyAlert) => ({
  id: String(a?.id ?? a?.uuid ?? a?.alert_source_id ?? ''),
  name: a?.name ?? '',
  deviceGroupId: a?.deviceGroupId ?? a?.device_group_id ?? undefined,
})

/** Safely pull an array of alert sources regardless of casing/wrapping */
const extractAlertSources = (raw: any): ReturnType<typeof normalizeAlertSource>[] => {
  const maybe =
    raw?.alertSources ??
    raw?.alert_sources ??
    raw?.alert_sources?.data ??
    raw?.deviceGroup?.alertSources ??
    raw?.deviceGroup?.alert_sources ??
    raw?.deviceGroup?.alert_sources?.data

  if (!maybe) return []
  const arr = Array.isArray(maybe) ? maybe : []
  return arr.map(normalizeAlertSource).filter(s => s.id)
}

const normalize = (g: any): DeviceGroup => {
  const raw = g?.deviceGroup ?? g
  return {
    id: buildId(raw),
    orgId: raw?.org_id ?? raw?.orgId ?? '',
    name: raw?.name ?? '',
    color: raw?.color,
    language: raw?.language,
    channelName: raw?.channel_name ?? raw?.channelName ?? '',
    wifiNetId: raw?.wifi_net_id ?? raw?.wifiNetId ?? '',
    insertedAt: toDate(raw?.inserted_at ?? raw?.insertedAt),
    updatedAt: toDate(raw?.updated_at ?? raw?.updatedAt),
    alertSources: extractAlertSources(raw),
  } as DeviceGroup
}

// Only send fields we control; backend sets timestamps and membership.
export type DeviceGroupUpsert = Omit<DeviceGroup, 'insertedAt' | 'updatedAt' | 'alertSources'>

export const getDeviceGroups = async (orgId: string): Promise<DeviceGroup[]> => {
  const res = await getRequest({
    endpoint: 'api/v5/device_groups',
    header: orgHeader(orgId),
  })
  const list = res?.data?.data ?? []
  return list.map((g: any) => normalize({ ...g, orgId }))
}

// CREATE: POST /api/v5/device_groups
// UPDATE: PUT  /api/v5/device_groups/{id}
export const upsertDeviceGroup = async (
  payload: Partial<DeviceGroupUpsert>
): Promise<DeviceGroup> => {
  const { id, orgId, ...rest } = payload
  const header = orgHeader(orgId || '')

  if (id) {
    // UPDATE
    const res = await putRequest({
      endpoint: `api/v5/device_groups/${id}`,
      header,
      data: { ...rest, orgId }, // channelName, color, language, name, orgId, wifiNetId
    })
    const body = res?.data?.data ?? res?.data
    return normalize(body)
  } else {
    // CREATE
    const res = await postRequest({
      endpoint: 'api/v5/device_groups',
      header,
      data: { ...rest, orgId }, // name required; include orgId
    })
    const body = res?.data?.data ?? res?.data
    return normalize(body)
  }
}

// DELETE: DELETE /api/v5/device_groups/{id}  -> 204
export const deleteDeviceGroup = async (id: string, orgId: string): Promise<void> => {
  await deleteRequest({
    endpoint: `api/v5/device_groups/${id}`,
    header: orgHeader(orgId),
  })
}

// ADD/REMOVE a device (alert source) to/from a device group
// PUT /api/v5/device_groups/{id}/add_alert_source/{alert_source_id}
// PUT /api/v5/device_groups/{id}/remove_alert_source/{alert_source_id}

/** Add an alert source (device) to a device group. */
export const addAlertSourceToDeviceGroup = async (
  deviceGroupId: string,
  alertSourceId: string,
  orgId: string
): Promise<DeviceGroup | void> => {
  const res = await putRequest({
    endpoint: `api/v5/device_groups/${deviceGroupId}/add_alert_source/${alertSourceId}`,
    header: orgHeader(orgId),
    data: {}, // some backends require a body for PUT
  })
  const body = res?.data?.data ?? res?.data
  return body ? normalize(body) : undefined
}

/** Remove an alert source (device) from a device group. */
export const removeAlertSourceFromDeviceGroup = async (
  deviceGroupId: string,
  alertSourceId: string,
  orgId: string
): Promise<DeviceGroup | void> => {
  const res = await putRequest({
    endpoint: `api/v5/device_groups/${deviceGroupId}/remove_alert_source/${alertSourceId}`,
    header: orgHeader(orgId),
    data: {},
  })
  const body = res?.data?.data ?? res?.data
  return body ? normalize(body) : undefined
}

/**
 * Convenience helper: move a device from its current group to a new group.
 * Pass `nextGroupId` as '' or undefined to just remove from the current group.
 */
export const setAlertSourceDeviceGroup = async (opts: {
  currentGroupId?: string | null
  nextGroupId?: string | null
  alertSourceId: string
  orgId: string
}): Promise<void> => {
  const { currentGroupId, nextGroupId, alertSourceId, orgId } = opts

  if (currentGroupId && currentGroupId !== nextGroupId) {
    await removeAlertSourceFromDeviceGroup(currentGroupId, alertSourceId, orgId)
  }
  if (nextGroupId) {
    await addAlertSourceToDeviceGroup(nextGroupId, alertSourceId, orgId)
  }
}
